import React, { useState } from "react";
import {
  FaClock,
  FaUsers,
  FaMapMarkerAlt,
  FaStar,
  FaChevronLeft,
  FaChevronRight,
  FaTag,
  FaCheck,
  FaMobileAlt,
  FaShieldAlt,
  FaCar,
  FaTimesCircle,
  FaLanguage,
} from "react-icons/fa";
import { useCurrency } from "../../context/CurrencyContext";
import { formatPriceSync } from "../../utils/currency";

const ActivityDetailsPage = ({
  selectedActivity,
  activityOptions,
  selectedCountry,
  selectedCity,
  API_BASE_URL,
  onContinue,
}) => {
  const { currency } = useCurrency();
  const [galleryIndex, setGalleryIndex] = useState(0);
  const images = selectedActivity?.images?.length
    ? selectedActivity.images
    : [];

  const baseUrl = API_BASE_URL?.replace("/api/activity", "") || "";
  const imageUrl = (path) =>
    !path
      ? null
      : path.startsWith("http")
        ? path
        : `${baseUrl}/${path.replace(/\\/g, "/")}`;

  const nextImage = () =>
    setGalleryIndex((i) => (i + 1) % Math.max(images.length, 1));
  const prevImage = () =>
    setGalleryIndex((i) =>
      i === 0 ? Math.max(images.length - 1, 0) : i - 1
    );

  return (
    <div className="space-y-8">
      {/* Hero – activity name + main image */}
      <div className="relative rounded-2xl overflow-hidden bg-slate-900 min-h-[280px] sm:min-h-[320px]">
        {images.length > 0 ? (
          <>
            <img
              src={imageUrl(images[galleryIndex])}
              alt={selectedActivity?.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={prevImage}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
                  aria-label="Previous image"
                >
                  <FaChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={nextImage}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
                  aria-label="Next image"
                >
                  <FaChevronRight className="w-4 h-4" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {images.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setGalleryIndex(i)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        i === galleryIndex ? "bg-white" : "bg-white/50"
                      }`}
                      aria-label={`Image ${i + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-teal-600 to-emerald-700 flex items-center justify-center">
            <span className="text-white/80 text-6xl font-bold tracking-tight">
              {selectedActivity?.name?.charAt(0) || "?"}
            </span>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
          <div className="flex flex-wrap gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/20 backdrop-blur-sm rounded-lg text-xs font-medium text-white">
              <FaClock className="w-3 h-3" /> Approx {selectedActivity?.duration || 60} mins
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500/90 rounded-lg text-xs font-medium text-white">
              <FaShieldAlt className="w-3 h-3" /> Instant Confirmation
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/20 backdrop-blur-sm rounded-lg text-xs font-medium text-white">
              <FaMobileAlt className="w-3 h-3" /> Mobile Voucher Accepted
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {selectedCity && (
              <span className="inline-flex items-center gap-1.5 text-sm text-white/90">
                <FaMapMarkerAlt className="w-3.5 h-3.5" />
                {selectedCity.name}
                {selectedCountry && `, ${selectedCountry.name}`}
              </span>
            )}
            <span className="inline-flex items-center gap-1 text-sm text-amber-300">
              <FaStar className="w-4 h-4" />
              <span className="font-semibold">4.8</span>
              <span className="text-white/80">(124 reviews)</span>
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
            {selectedActivity?.name}
          </h1>
          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-white/90">
            <span className="inline-flex items-center gap-1.5">
              <FaClock className="w-4 h-4 text-white/70" />
              {selectedActivity?.duration || "—"} mins
            </span>
            <span className="inline-flex items-center gap-1.5">
              <FaUsers className="w-4 h-4 text-white/70" />
              Max {selectedActivity?.maxCapacity || "—"} people
            </span>
            <span
              className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                selectedActivity?.status === "active"
                  ? "bg-emerald-500/90 text-white"
                  : "bg-amber-500/90 text-white"
              }`}
            >
              {selectedActivity?.status === "active"
                ? "Available"
                : "Limited slots"}
            </span>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/20 backdrop-blur-sm rounded-lg text-xs text-white">
              <FaCheck className="w-3.5 h-3.5 text-emerald-300" />
              Instant Confirmation
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/20 backdrop-blur-sm rounded-lg text-xs text-white">
              <FaMobileAlt className="w-3.5 h-3.5" />
              Mobile Voucher
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/20 backdrop-blur-sm rounded-lg text-xs text-white">
              <FaCar className="w-3.5 h-3.5" />
              Transfer options
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/20 backdrop-blur-sm rounded-lg text-xs text-white">
              <FaTimesCircle className="w-3.5 h-3.5 text-amber-300" />
              Free cancellation 24h prior
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/20 backdrop-blur-sm rounded-lg text-xs text-white">
              <FaLanguage className="w-3.5 h-3.5" />
              English
            </span>
          </div>
        </div>
      </div>

      {/* Thumbnail strip – all images */}
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <style>{`.scrollbar-hide::-webkit-scrollbar { display: none; }`}</style>
          {images.map((img, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setGalleryIndex(i)}
              className={`flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden border-2 transition-all ${
                i === galleryIndex
                  ? "border-blue-500 ring-2 ring-blue-200"
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <img
                src={imageUrl(img)}
                alt={`${selectedActivity?.name} ${i + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Highlights & Description */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-3">{selectedActivity?.name} Highlights</h2>
        <p className="text-slate-600 leading-relaxed whitespace-pre-line mb-6">
          {selectedActivity?.description ||
            "Discover an unforgettable experience. Perfect for groups and solo travellers alike."}
        </p>
        <h3 className="text-base font-semibold text-gray-900 mb-2">How to Redeem</h3>
        <ul className="text-sm text-slate-600 space-y-2 list-disc list-inside">
          <li>Upon arrival, present your voucher (printed or on mobile) for verification.</li>
          <li>You will receive a confirmation voucher via email shortly after booking.</li>
          <li>Check your spam folder if you do not see the confirmation in your inbox.</li>
        </ul>
      </section>

      {/* Packages overview */}
      <section className="rounded-2xl border border-slate-200 bg-slate-50/50 p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <FaTag className="text-blue-600 text-lg" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Packages</h2>
            <p className="text-sm text-slate-600">
              {activityOptions?.length || 0} options available. Choose one in the next step.
            </p>
          </div>
        </div>
        {activityOptions?.length > 0 ? (
          <ul className="space-y-3 mb-6">
            {activityOptions.map((opt) => (
              <li
                key={opt._id}
                className="flex flex-wrap items-center justify-between gap-3 p-4 bg-white rounded-xl border border-slate-200"
              >
                <div className="flex items-center gap-3">
                  <FaCheck className="text-emerald-500 w-4 h-4 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-gray-900">{opt.name}</span>
                    <div className="flex items-center gap-3 mt-0.5 text-sm text-slate-500">
                      <span>{opt.duration} min</span>
                      <span>Max {opt.maxCapacity} people</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-lg font-bold text-gray-900">
                    {formatPriceSync(Number(opt.basePrice || 0), currency)}
                  </span>
                  <span className="text-xs text-slate-500 block">per person</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-slate-500 text-sm py-2">Loading packages…</p>
        )}

        <button
          type="button"
          onClick={onContinue}
          className="w-full sm:w-auto min-w-[200px] px-6 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
        >
          Choose Package
          <FaChevronRight className="w-4 h-4" />
        </button>
      </section>
    </div>
  );
};

export default ActivityDetailsPage;
